// RuleScheduleFields — timing section for a rule's "When" form.
// Anchor triggers (check_in/check_out): show before/after toggle + value + unit + optional at-time.
// Event triggers (booking.confirmed etc): show "immediately after event" label only.


import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { type KnownTrigger, type TimingFormState, type TimingUnit, ANCHOR_TRIGGERS } from '@/lib/rules';

interface RuleScheduleFieldsProps {
  trigger: KnownTrigger;
  timing: TimingFormState;
  onChange: (t: TimingFormState) => void;
  disabled?: boolean;
}

const UNIT_OPTIONS: { value: TimingUnit; label: string }[] = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
];

export function RuleScheduleFields({ trigger, timing, onChange, disabled }: RuleScheduleFieldsProps) {
  const isAnchor = ANCHOR_TRIGGERS.includes(trigger as (typeof ANCHOR_TRIGGERS)[number]);

  if (!isAnchor) {
    return <p className="text-sm text-muted">Fires immediately when the event occurs.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Before / After toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={timing.direction === 'before' ? 'primary' : 'secondary'}
          size="sm"
          disabled={disabled}
          onClick={() => onChange({ ...timing, direction: 'before' })}
        >
          Before
        </Button>
        <Button
          type="button"
          variant={timing.direction === 'after' ? 'primary' : 'secondary'}
          size="sm"
          disabled={disabled}
          onClick={() => onChange({ ...timing, direction: 'after' })}
        >
          After
        </Button>
      </div>

      {/* Value + Unit row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-1">
          <Input
            label="Amount"
            type="number"
            min={0}
            step={1}
            value={timing.value}
            onChange={(e) =>
              onChange({ ...timing, value: Math.max(0, parseInt((e.target as HTMLInputElement).value, 10) || 0) })
            }
            disabled={disabled}
            className="w-24"
          />
        </div>
        <div className="flex-1">
          <Select
            label="Unit"
            value={timing.unit}
            options={UNIT_OPTIONS}
            onChange={(e) => onChange({ ...timing, unit: e.target.value as TimingUnit })}
            disabled={disabled}
          />
        </div>
      </div>

      {/* At local time (optional) */}
      <Input
        label="At (local time, optional)"
        type="time"
        value={timing.atLocalTime}
        onChange={(e) => onChange({ ...timing, atLocalTime: (e.target as HTMLInputElement).value })}
        disabled={disabled}
        placeholder="HH:MM"
        hint="Leave blank to fire as soon as the offset is reached."
        className="max-w-xs"
      />
    </div>
  );
}
